/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import type {
  AnimationState,
  ApplicationConfig,
  ApplicationConfigurationState,
  FeaturesState,
  PluginManager,
  ScopeEventMap,
  ScopeName,
  ScopeStateMap,
  TimeState,
} from '@opengeoweb/plugin-interface';
import { GEOWEB_AUTH_ISSUER } from '@opengeoweb/plugin-interface';
import type { ConfigType } from '@opengeoweb/shared';
import type {
  CoreAppStore,
  syncGroupsTypes,
  commonTypes,
} from '@opengeoweb/store';
import {
  commonSelectors,
  genericSelectors,
  mapSelectors,
} from '@opengeoweb/store';
import type { IsEventRoundTrip } from '../../hooks';
import { useSendOnReduxChange, useSetScopeStateOnChange } from '../../hooks';

interface UseReduxToPluginOptions {
  pluginBridge: PluginManager;
  sourceId: string;
  hasScope: (scope: ScopeName) => boolean;
  applicationConfig?: ConfigType;
}

interface AuthSource {
  isLoggedIn: boolean;
  credentials: commonTypes.Credentials | undefined;
}

const toApplicationConfig = (config: ConfigType): ApplicationConfig => ({
  frontend: {
    global: {
      sentryEnabled: config.GW_FEATURE_INITIALIZE_SENTRY,
      theme: config.GW_DEFAULT_THEME,
      language: config.GW_LANGUAGE,
      initialPresetsFilename: config.GW_INITIAL_PRESETS_FILENAME,
    },
  },
  backend: {
    trajks: {
      baseUrl: config.GW_KNMI_PUBLISHER_BASE_URL,
      workspaceId: config.GW_TRAJKS_WORKSPACE_ID,
    },
  },
});

export const useReduxToPlugin = ({
  pluginBridge,
  sourceId,
  hasScope,
  applicationConfig,
}: UseReduxToPluginOptions): void => {
  const timeValue = useSelector((store: CoreAppStore) =>
    genericSelectors.getTime(
      genericSelectors.getSynchronizationGroupStore(store),
      sourceId,
    ),
  );
  const animationStartTime = useSelector((store: CoreAppStore) =>
    mapSelectors.getAnimationStartTime(store, sourceId),
  );
  const animationEndTime = useSelector((store: CoreAppStore) =>
    mapSelectors.getAnimationEndTime(store, sourceId),
  );
  const animationState = useMemo(
    () => ({ start: animationStartTime, end: animationEndTime }),
    [animationStartTime, animationEndTime],
  );
  const sharedData = useSelector((store: CoreAppStore) =>
    genericSelectors.selectSharedData(store, sourceId),
  );
  const isLoggedIn = useSelector((store: CoreAppStore) =>
    commonSelectors.getIsLoggedIn(store),
  );
  const credentials = useSelector((store: CoreAppStore) =>
    commonSelectors.getCredentials(store),
  );
  const authSource: AuthSource = useMemo(
    () => ({ isLoggedIn, credentials }),
    [isLoggedIn, credentials],
  );

  const toTimeEvent = useCallback(
    (time: typeof timeValue) =>
      time?.currentTime
        ? {
            type: 'setTime' as const,
            time: time.currentTime.replace(/\.\d{3}Z$/, 'Z'),
          }
        : null,
    [],
  );

  const toAnimationEvent = useCallback(
    (anim: { start: string | undefined; end: string | undefined }) =>
      anim.start !== undefined && anim.end !== undefined
        ? {
            type: 'setAnimationTimeSpan' as const,
            startTime: anim.start,
            endTime: anim.end,
          }
        : null,
    [],
  );

  const toFeaturesEvent = useCallback(
    (data: syncGroupsTypes.PotentialData): ScopeEventMap['features'] | null => {
      const [layer] = Object.values(data.featureLayersById ?? {});
      if (!layer) {
        return null;
      }
      return {
        type: 'setLayerFeatures',
        layerId: layer.layerId,
        featureCollection: layer.geoJSON,
      };
    },
    [],
  );

  const toApplicationConfigurationState = useCallback(
    (config: ConfigType | undefined): ApplicationConfigurationState | null =>
      config && Object.keys(config).length > 0
        ? { geowebConfig: toApplicationConfig(config) }
        : null,
    [],
  );

  const toUserScopeState = useCallback(
    ({ isLoggedIn, credentials }: AuthSource): ScopeStateMap['user'] | null => {
      if (!credentials) {
        return {
          isLoggedIn: false,
          user: null,
          hasConnectionIssue: false,
        };
      }
      return {
        isLoggedIn,
        user: isLoggedIn
          ? {
              username: credentials.username,
              // Currently not used in the opengeoweb
              firstName: '',
              lastName: '',
              rolesPerIssuer: [
                {
                  issuer: GEOWEB_AUTH_ISSUER,
                  roles: (credentials.roles ?? []).map((role) => role.name),
                },
              ],
            }
          : null,
        hasConnectionIssue: credentials.has_connection_issue ?? false,
      };
    },
    [],
  );

  const toAuthenticationScopeState = useCallback(
    ({
      isLoggedIn,
      credentials,
    }: AuthSource): ScopeStateMap['authentication'] | null => {
      if (!credentials || !isLoggedIn) {
        return { tokens: [] };
      }
      return {
        tokens: [
          {
            issuer: GEOWEB_AUTH_ISSUER,
            token: credentials.token,
            refreshToken: credentials.refresh_token ?? '',
            roles: (credentials.roles ?? []).map((role) => role.name),
            ...(credentials.expires_at
              ? { tokenExpiresAt: credentials.expires_at * 1000 }
              : { tokenExpiresAt: 0 }),
          },
        ],
      };
    },
    [],
  );

  // Return true when a Redux event already matches the bridge state (plugin echo), preventing plugin → Redux → plugin loops without blocking real external updates.
  const isTimeEventRoundTrip = useCallback<IsEventRoundTrip<'time'>>(
    (event: ScopeEventMap['time'], bridgeState: TimeState | undefined) =>
      bridgeState?.time === event.time,
    [],
  );

  const isAnimationEventRoundTrip = useCallback<IsEventRoundTrip<'animation'>>(
    (
      event: ScopeEventMap['animation'],
      bridgeState: AnimationState | undefined,
    ) => {
      if (!bridgeState) {
        return false;
      }
      switch (event.type) {
        case 'setAnimationStartTime':
          return bridgeState.animationStartTime === event.time;
        case 'setAnimationEndTime':
          return bridgeState.animationEndTime === event.time;
        case 'setAnimationTimeSpan':
          return (
            bridgeState.animationStartTime === event.startTime &&
            bridgeState.animationEndTime === event.endTime
          );
        default:
          return false;
      }
    },
    [],
  );

  const isFeaturesEventRoundTrip = useCallback<IsEventRoundTrip<'features'>>(
    (
      event: ScopeEventMap['features'],
      bridgeState: FeaturesState | undefined,
    ) => {
      if (!bridgeState || event.type !== 'setLayerFeatures') {
        return false;
      }
      const layer = bridgeState.featureLayers.find(
        (currentLayer) => currentLayer.id === event.layerId,
      );
      return (
        !!layer &&
        JSON.stringify(layer.featureCollection) ===
          JSON.stringify(event.featureCollection)
      );
    },
    [],
  );

  useSendOnReduxChange(
    pluginBridge,
    'time',
    timeValue,
    toTimeEvent,
    hasScope('time'),
    isTimeEventRoundTrip,
  );
  useSendOnReduxChange(
    pluginBridge,
    'animation',
    animationState,
    toAnimationEvent,
    hasScope('animation'),
    isAnimationEventRoundTrip,
  );
  useSendOnReduxChange(
    pluginBridge,
    'features',
    sharedData,
    toFeaturesEvent,
    hasScope('features'),
    isFeaturesEventRoundTrip,
  );
  useSetScopeStateOnChange(
    pluginBridge,
    'applicationConfiguration',
    applicationConfig,
    toApplicationConfigurationState,
    hasScope('applicationConfiguration'),
  );
  useSetScopeStateOnChange(
    pluginBridge,
    'user',
    authSource,
    toUserScopeState,
    hasScope('user'),
  );
  useSetScopeStateOnChange(
    pluginBridge,
    'authentication',
    authSource,
    toAuthenticationScopeState,
    hasScope('authentication'),
  );
};
